// src/components/watchlist/NotificationsMain.js
import React, { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  alpha,
  Snackbar,
  Alert,
} from '@mui/material'
import { Add as AddIcon, NotificationsActive as NotificationsActiveIcon } from '@mui/icons-material'
import { motion } from 'framer-motion'
import SubscriptionsList from './SubscriptionsList'
import AddSubscription from './AddSubscription'
import DeleteConfirmationDialog from './DeleteConfirmationDialog'
import SubscriptionDetailsDialog from './SubscriptionDetailsDialog'
import { useTranslation } from 'react-i18next'

const NotificationsMain = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  }

  // States
  const [user, setUser] = useState(null)
  const [subscriptions, setSubscriptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [isAddingSubscription, setIsAddingSubscription] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState(null)
  const [editingSubscriptionId, setEditingSubscriptionId] = useState(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [filterType, setFilterType] = useState('all')
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' })

  // Fetch user and subscriptions data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data, error } = await supabase.auth.getUser()
        if (error) throw error
        if (data?.user) {
          setUser(data.user)
          fetchSubscriptions(data.user.id)
        }
      } catch (error) {
        console.error('Error fetching user:', error)
        showAlert(error.message, 'error')
      }
    }
    fetchUser()
  }, [])

  // Fetch subscriptions from Supabase
  const fetchSubscriptions = async (userId) => {
    try {
      setLoading(true)
      const { data, error } = await supabase.from('notifications_with_listings_count').select('*')
      
      if (error) throw error
      setSubscriptions(data || [])
    } catch (error) {
      console.error('Error fetching subscriptions:', error)
      showAlert(t('notifications.errorFetchSubscriptions'), 'error')
    } finally {
      setLoading(false)
    }
  }

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)

    if (newValue === 0) {
      setFilterType('all')
    } else if (newValue === 1) {
      setFilterType('rent')
    } else if (newValue === 2) {
      setFilterType('buy')
    }
  }

  // Filter subscriptions based on selected tab
  const getFilteredSubscriptions = () => {
    if (filterType === 'all') return subscriptions
    return subscriptions.filter((sub) => sub.type === filterType)
  }

  // Start editing a subscription's price range
  const startEditingPrice = (subscription) => {
    setEditingSubscriptionId(subscription.id)
  }

  // Cancel price range editing
  const cancelEditingPrice = () => {
    setEditingSubscriptionId(null)
  }

  // Update a subscription's price range
  const handleUpdatePrice = async (id, minPrice, maxPrice) => {
    try {
      // Validate input
      if (minPrice > maxPrice) {
        showAlert(t('notifications.minMustBeLessOrEqual'), 'error')
        return false
      }

      const { error } = await supabase
        .from('notifications')
        .update({ min_price: minPrice, max_price: maxPrice })
        .eq('id', id)

      if (error) throw error

      // Update local state
      setSubscriptions((prev) =>
        prev.map((sub) =>
          sub.id === id ? { ...sub, min_price: minPrice, max_price: maxPrice } : sub
        )
      )

      showAlert(
        t('notifications.successUpdatePrice', 'Price range updated successfully'),
        'success'
      )
      return true
    } catch (error) {
      console.error('Error updating price:', error)
      showAlert(t('notifications.errorUpdatePrice'), 'error')
      return false
    }
  }

  // Toggle subscription enabled/disabled state
  const handleToggleSubscription = async (id, currentEnabled) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ enabled: !currentEnabled })
        .eq('id', id)

      if (error) throw error

      // Update local state
      setSubscriptions((prev) =>
        prev.map((sub) => (sub.id === id ? { ...sub, enabled: !currentEnabled } : sub))
      )

      showAlert(
        currentEnabled
          ? t('notifications.successDisabled', 'Subscription disabled')
          : t('notifications.successEnabled', 'Subscription enabled'),
        'success'
      )
    } catch (error) {
      console.error('Error toggling subscription:', error)
      showAlert(t('notifications.errorToggleSubscription'), 'error')
    }
  }

  // Confirm deletion dialog
  const confirmDeleteSubscription = (id) => {
    setSubscriptionToDelete(id)
    setConfirmDeleteOpen(true)
  }

  // Delete subscription after confirmation
  const handleDeleteSubscription = async () => {
    if (!subscriptionToDelete) return

    try {
      const { error } = await supabase.from('notifications').delete().eq('id', subscriptionToDelete)

      if (error) throw error

      // Update local state
      setSubscriptions((prev) => prev.filter((sub) => sub.id !== subscriptionToDelete))
      showAlert(t('notifications.successDelete', 'Subscription deleted successfully'), 'success')
    } catch (error) {
      console.error('Error deleting subscription:', error)
      showAlert(t('notifications.errorDeleteSubscription'), 'error')
    } finally {
      setConfirmDeleteOpen(false)
      setSubscriptionToDelete(null)
    }
  }

  // View a subscription's details
  const handleViewSubscription = (subscription) => {
    setSelectedSubscription(subscription)
  }

  // Close subscription details view
  const handleCloseSubscriptionView = () => {
    setSelectedSubscription(null)
  }

  // Handle adding a new subscription
  const handleAddSubscription = (newSubscription) => {
    // Update local state with the new subscription
    setSubscriptions((prev) => [newSubscription, ...prev])
    setIsAddingSubscription(false)
    showAlert(t('notifications.successAdd', 'Subscription added successfully'), 'success')
  }

  // Show an alert message
  const showAlert = (message, severity = 'info') => {
    setAlert({
      open: true,
      message,
      severity,
    })
  }

  // Close alert
  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false })
  }

  // Render main component
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 3, px: { xs: 2, sm: 3 } }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 2,
          background: (theme) =>
            `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.2)}, ${alpha(
              theme.palette.primary.main,
              0.1
            )})`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            right: -50,
            bottom: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: (theme) =>
              `radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.2
              )} 0%, transparent 70%)`,
            zIndex: 0,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <NotificationsActiveIcon fontSize="large" color="primary" />
            {t('notifications.title')}
          </Typography>

          <Typography variant="body1" paragraph color="text.secondary" sx={{ maxWidth: 800 }}>
            {t(
              'notifications.description',
              'Create customized alerts for properties that match your criteria. Define areas on the map or search for specific locations, set your preferences, and get notified when new properties are listed.'
            )}
          </Typography>

          {!isAddingSubscription && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsAddingSubscription(true)}
              sx={{ mt: 1 }}
            >
              {t('notifications.addSubscription')}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Add Subscription Section */}
      {isAddingSubscription && (
        <AddSubscription
          user={user}
          onCancel={() => setIsAddingSubscription(false)}
          onSuccess={handleAddSubscription}
          showAlert={showAlert}
        />
      )}

      {/* Subscriptions List Section */}
      {!isAddingSubscription && (
        <motion.div variants={fadeIn} initial="hidden" animate="visible">
          <Box sx={{ mb: 3 }}>
            <Paper sx={{ borderRadius: 2 }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                textColor="primary"
                indicatorColor="primary"
                aria-label="property alerts tabs"
              >
                <Tab label={t('notifications.allAlerts', 'All Alerts')} />
                <Tab label={t('notifications.rentAlerts', 'Rentals')} />
                <Tab label={t('notifications.buyAlerts', 'For Sale')} />
              </Tabs>
            </Paper>
          </Box>

          <SubscriptionsList
            loading={loading}
            subscriptions={getFilteredSubscriptions()}
            editingSubscriptionId={editingSubscriptionId}
            onToggleSubscription={handleToggleSubscription}
            onEditPrice={startEditingPrice}
            onCancelEdit={cancelEditingPrice}
            onUpdatePrice={handleUpdatePrice}
            onViewSubscription={handleViewSubscription}
            onDeleteSubscription={confirmDeleteSubscription}
            onAddSubscription={() => setIsAddingSubscription(true)}
          />
        </motion.div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteSubscription}
      />

      {/* Subscription Details Dialog */}
      {selectedSubscription && (
        <SubscriptionDetailsDialog
          subscription={selectedSubscription}
          onClose={handleCloseSubscriptionView}
        />
      )}

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default NotificationsMain
